/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */

/**
 * Dyn.PlotElement() - base class for plot objects that are added/removed from plot.
 *  - derived classes include MeanElement, DotPlotElement, RefLineElement, etc
 *  - determines the size/shape/location of the PlotView's case icons
 *  - creates+positions the PlotView's adornments (visible elements that are not case icons, e.g. mean line)
 */
export class PlotElement {
    constructor( Dyn ) {
        this.dyn = Dyn;
        this.plotShaper = null; // Dyn.PlotShaper, reference to the parent object that has a array of Dyn.PlotElement
        this.primaryType = '';   // {Dyn.EPlotControl} primary type, for ease of IDing plot elements
        this.replaceType = '';   // {Dyn.EPlotControl} replacement type, for this.replaces()
        this.priority = Number.NaN;     // {Dyn.EPlotPriority} number, lower == compute first when recalculating plot
        //this.isSuppressed	= false; // boolean
        this.glowElement = null;
    }

    static kDefaultTitle = '';  // derived classes should override
    static kUndoAction = '';     // derived classes should override

    /**
     * initialize a Dyn.PlotElement
     */
    initElement( types, optionalDyn ) {
        // TODO: remove this construction once all subclasses use new class format with Dyn as 1st parameter
        // to the constructor.
        if( ! this.dyn )
            this.dyn = optionalDyn;

        var Dyn = this.dyn,
            thisPlotElement = this;

        Dyn.assert( typeof types === 'object' );
        Dyn.assert( typeof types.priority === 'number' );  	 // type Dyn.EPlotPriority
        Dyn.assert( typeof types.replaceType === 'string' ); // type Dyn.EPlotControl
        Dyn.assert( typeof types.primaryType === 'string' ); // type Dyn.EPlotControl
        Dyn.assert( types.otherControls instanceof Array, "array required" ); // [] of Dyn.EPlotControl

        this.priority = types.priority;		// determines calculation order of elements
        this.replaceType = types.replaceType;	// what element this automatically replaces if replaces() is not overridden.
        this.primaryType = types.primaryType;	// unique key for each constructor, for save/restore

        // set each control property to true, so plotElements and plotShaper can make queries from values.
        this[this.replaceType] = true;
        this[this.primaryType] = true;
        types.otherControls.forEach( ( value ) => {
            Dyn.assertHasValue( Dyn.EPlotControl, value );
            thisPlotElement[value] = true;	// e.g. this.controlsDotPlot = true;
        } );

        // check values
        Dyn.assertHasValue( Dyn.EPlotPriority, this.priority );
        Dyn.assertHasValue( Dyn.EPlotControl, this.primaryType );
        Dyn.assertHasValue( Dyn.EPlotControl, this.replaceType );
        Dyn.assert( this.priority !== Dyn.EPlotPriority.kPriorityLAST && Number.isFinite( this.priority ));
        Dyn.assert( this.primaryType );
        Dyn.assert( this.replaceType );

        // this.plotShaper reference is null on construction, then non-null after constructed PlotElement
        // is added to the plot, by PlotShaper.addElement().
        Dyn.assert( this.plotShaper === null );
        //console.log("PlotElement.initElement", this.primaryType );
    }

    /** check common values after plot element is added to plot. */
    debugValidate() {
        let Dyn = this.dyn;
        Dyn.assert( this.plotShaper );
        Dyn.assert( Object.values(Dyn.EPlotPriority).includes( this.priority )
            && this.priority !== Dyn.EPlotPriority.kPriorityLAST && Number.isFinite( this.priority ));
        Dyn.assert( Object.values(Dyn.EPlotControl).includes( this.primaryType ) && this.primaryType );
        Dyn.assert( Object.values(Dyn.EPlotControl).includes( this.replaceType ) && this.replaceType );
        if( this[ Dyn.EPlotControl.kControlsShape]) {
            Dyn.assert( Dyn.dataUtil.isNonEmptyString( this.kDefaultTitle ), "Plot Shaper is missing kDefaultTitle: "+this.primaryType );
        }
        Dyn.assert( Dyn.dataUtil.isNonEmptyString( this.getUndoAction() ), "Plot Element is missing kUndoAction: "+this.primaryType );
    }

    /**
     * Save the PlotElement to a data object that can be JSON-ified
     * for the custom implementation for particular plot move save/restore
     * to those plot elements.
     * See also savePlotElement()
     * @returns {{}} - data to be used by restore()
     */
    save() {
        "use strict";
        var plotElementModel = {};

        plotElementModel.primaryType = this.primaryType; // determines what Dyn.PlotElement subtype to restore
        // #1335 for plot elements that control shape, don't override saved plot or axis titles; instead use saved title even if blank
        // #1379 no longer save or restore plotElementModel.needsDefaultTitle; this parameter used on initialization only
        //plotElementModel.needsDefaultTitle = this.needsDefaultTitle;
        return plotElementModel;
    }

    /**
     * Change our PlotElement to match the saved data.
     * See also restorePlotElement()
     */
    restore( savedData ) {
        "use strict";
        // Note:
        // The primary type may change, because Dyn.PlotElemHelper.registerType() may restore an old type to a new for backward compatibility
        // Therefore we don't restore the saved .primaryType, but instead use the constructor's default .primaryType
        // (and this is OK to fail): Dyn.assert( this.primaryType === savedData.primaryType );

        // #1335 for plot elements that control shape, don't override saved plot or axis titles; instead use saved title even if blank
        // #1379 no longer save or restore plotElementModel.needsDefaultTitle; this parameter used on initialization only
        // if( this[Dyn.EPlotControl.kControlsShape] && this.hasOwnProperty("needsDefaultTitle")) {
        //     this.needsDefaultTitle = Boolean( savedData.needsDefaultTitle );
        // }
    }

    /**
     * Destroy a Dyn.PlotElement, by undoing the cumulative
     * effects of constructing one.
     * If derived class is overriding then call at end:
     *  super.destroy();
     */
    destroy() {
        "use strict";
        // remove our adornments from the plot, if any
        if( this.plotShaper && this.plotShaper.adornments ) {
            this.plotShaper.adornments.removeAdornmentsArray( this.primaryType );
        }
        // remove our measures from the Summary View, if any
        if( this.plotShaper && this.plotShaper.measures ) {
            this.plotShaper.measures.removeMeasuresArray( this.primaryType );
        }
        // remove our measures from the Sample Statistics, if any
        if( this.plotShaper && this.plotShaper.sampleStats ) {
            this.plotShaper.sampleStats.removeMeasureFromSampleStats( this.primaryType );
        }
        this.removeGlow();
        this.plotShaper = null;
        this[this.primaryType] = false;
    }

    toString() {
        "use strict";
        return '{PlotElement replaceType='+this.replaceType+' primaryType='+this.primaryType+' priority='+this.priority+'}';
    }

    /*
     * get currently active plot's summary in text format, it can be used for
     * voice over - accessibility.
     */
    getPlotSummary( options ) {
        let Dyn = this.dyn,
            plotSummary = [];
        if( options.wantPrimary && this.primaryType in Dyn.plotPrimaryTypeMap ){
            let totalCases = Dyn.plotView.dataSet.getPopulationSize() - Dyn.dataSet.missingCases.getNumCasesMissing(),
                plotName = Dyn.plotPrimaryTypeMap[this.primaryType];
            if( this.primaryType === Dyn.EPlotControl.kControlsDotPlot) {
                let isScatterPlot = ( Dyn.plotView.xAxis.isNumericAxis() && Dyn.plotView.yAxis.isNumericAxis())
                if ( isScatterPlot ){
                    plotName = Dyn.plotTitles.SCATTER_PLOT;
                }
            }
            // check if it primary ones, and all others we can skip it for now. e.g. count, mean etc.
            if( plotName ) {
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.plotTypeSummary, totalCases, plotName ));
            }
        }
        return plotSummary.join(' ');
    }

    /**
     * Return true if this (new) plot element should replace the given existing plot element.
     * If true, the caller should should delete iExistingPlotElement from the current plot.
     * Used to automatically remove incompatible plot elements by type.
     * Also used to copy parameters before removal, or modify and not remove the existing element.
     * Derived classes can override -- this base class simple removes others of same .replaceType ID
     * See also replacePlotElement()
     * @param iExistingPlotElement {Dyn.PlotElement} - existing element to examine/modify/remove
     * @param ioPlotAdornments - plot adornments, that may also be examined/modified/removed
     * @return {boolean} - true if iExistingPlotElement should be removed from plot
     */
    replaces( iExistingPlotElement, ioPlotAdornments ) {
        let Dyn = this.dyn;
        Dyn.assert( this.replaceType !== Dyn.EPlotControl.kControlsNothing, 'invalid control type' );
        return( this.replaceType === iExistingPlotElement.replaceType && // replace others of same control type
            this !== iExistingPlotElement ); // but never replace ourself!
    }

    //calcMissingValues() {
    //},

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef) {
        "use strict";
    }

    /**
     * Update the allowable number of case icons show per data case, normally 1.
     * PlotElements that control the plot shape can override this function, and return a 1+ value.
     */
    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        "use strict";
        return 1;
    }

    /**
     * Return the allowable number of case icons show per data case, normally 1.
     *
     * Here we return 2+ if there are multiple attributes on Y or X, but not both.
     * We allow multiple attributes on one axis only.
     *
     * For example user drops two numeric attributes "male salary",  "female salary" on the Y axis,
     * for a dataset with 15 cases, and dotplot wants to plot 30 cases total, one for each
     * attribute on the Y axis, so we return 2.
     *
     * Result
     * returns {number} icons per case,
     * sets {number} this.numAttributesUsedY
     * sets {number} this.numAttributesUsedX
     */
    calcNumIconsAllowOneMultiAttributeAxis( ioWhatChanged, subViewRef ) {
        "use strict";
        var Dyn = this.dyn,
            combinedAttributesX = subViewRef.xAxisView.allAttributes,
            combinedAttributesY = subViewRef.yAxisView.allAttributes,
            iconsPerCase;

        // 	first check the Y axis (favor Y axis)
        this.numAttributesUsedY = Math.min(
            ( subViewRef.yAxisView.isNumericActiveOrInactive() ? Dyn.plotUtil.kMaxAttributesOnAxis : 1 ),
            combinedAttributesY.count());
        // 	then check the X axis (only if Y is not multi-attribute)
        this.numAttributesUsedX = Math.min(
            (( subViewRef.xAxisView.isNumericActiveOrInactive() && this.numAttributesUsedY < 2 ) ? Dyn.plotUtil.kMaxAttributesOnAxis : 1 ),
            combinedAttributesX.count());

        // max of number of icons used on any one axis, and no less than 1.
        iconsPerCase = Math.max( 1, Math.max( this.numAttributesUsedX, this.numAttributesUsedY ));

        return iconsPerCase;
    }

    /**
     * Record which cases that have missing values for the given plot.
     * For example, if the 3rd case X or Y attribute of a scatterplot has a non-numeric value,
     * mark the 3rd case as missing.  e.g. this.plotShaper.dataSet.missingCases.setCaseIsMissing( 3, true)
     * PlotElements that control the plot shape can override this function.
     */
    calcMissingValueCases( numCases, xAxisView, yAxisView ) {
        "use strict";
        // default is to do nothing
    }

    /**
     * adjust the axis numeric range based on the data in the plot.
     */
    calcPlotSpecificRange( numCases, subViewRef ) {
        "use strict";
        // default is to do nothing
    }

    /** update the case positional data (shared by all elements) and adornments (specific to this element) */
    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments /*, topView */ ) {
        "use strict";
        // default is to do nothing
    }

    /**
     * Update the adornments array for this plot element.  The adornments array are visible Rafael elements controlled
     * by this plot element, in the order which this element expects.
     * @param ioAdornArray [] - Rafael elements this plot element controls.
     * @param iconPositions [] - reference to case icon positions previously updated by calcPosition()
     * @param xAxisView {Dyn.AxisView} - reference to X-axis previously updated by calcAxis()
     * @param yAxisView {Dyn.AxisView} - reference to Y-axis previously updated by calcAxis()
     * @param noAnimation {boolean} - true if this should be a quick update without animation of adornments.
     */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
        "use strict";
    }

    /** update the case icon style. Elements that control shape and want a rectangle or circle should override. */
    calcIconStyle( ioPositions, xAxisView, yAxisView ) {
        let Dyn = this.dyn;
        if( this[ Dyn.EPlotControl.kControlsShape] ) {	// e.g. Dyn.DotPlotElement
            // default: set icon style to a circle
            this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( ioPositions.length );
        }
        // if element does not control shape, it should not control icon style either
    }

    /**
     * Mark which axis bin this case is in.
     * If the x or y axis is separated (with numeric or categorical bins),
     * then assume pos.x and pos.y have the correct bin index (e.g. from calcValueToPlot())
     * if an axis is not separated, then there is only one bin, bin zero.
     * @param pos
     * @param xIsSeparated
     * @param yIsSeparated
     */
    calcBinIndex( pos, xIsSeparated, yIsSeparated ) {
        "use strict";
        var xi = ( xIsSeparated ? pos.x : 0 ),
            yi = ( yIsSeparated ? pos.y : 0 );
        if( pos.isVisible ) {
            pos.xBinIndex = xi;
            pos.yBinIndex = yi;
            pos.cellIndex = this.plotShaper.calcCellIndex( xi, yi );
        } else {
            pos.xBinIndex = pos.yBinIndex = pos.cellIndex = -1;
        }
        pos.sectionIndex = -1; // invalid unless plot creates sections, if so it is set by calcPosition()
    }

    /**
     * Alows each element to respond to an axis button click
     * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
     * @param subViewRef {*} -- references to xAxisView, yAxisView, topMargin, rightMargin
     * @param axisData {*} -- what button was clicked on what axis
     * @returns {boolean} -- return true if a Dyn.PlotElement responds to the button.
     */
    handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
        "use strict";
        return false; // default is to do nothing
    }

    /**
     * populate cells with cases[],
     * which has the index of each icon in the cell
     * NOTE: the icon index is not the same as the case index if using multiple attributes on an axis.
     * TODO: rename bins.cells.cases[] to bins.cells.iconIndices[],
     * TODO: rename bins.maxCasesInCells, bins.numExcludedCases also?
     */
    populateCellsWithCases( ioPositions ) {
        "use strict";
        var Dyn = this.dyn,
            plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            numCases = ioPositions.length,
            numExcludedCases = 0,
            maxCases = 0,
            i, pos, cell;

        // populate cells with cases[], index of each case in the cell
        //console.log( "populateCellsWithCases()", plotCells.length, (plotCells.length>0&&plotCells[0].cases)?"updating cell cases":"CREATING cell cases");
        for( i=0; i<numCells; i++ ) {
            plotCells[i].cases = []; // replace old case array if any
        }
        for( i=0; i<numCases; i++ ) {
            pos = ioPositions[i];
            if( pos.cellIndex >= 0 ) { // exclude cases not in any cell (missing value)
                Dyn.assertArrayIndex( plotCells, pos.cellIndex, "cell index error" );
                plotCells[ pos.cellIndex ].cases.push( i );
            } else {
                ++numExcludedCases;
            }
        }
        for( i=0; i<numCells; i++ ) {
            maxCases = Math.max( maxCases, plotCells[i].cases.length );
        }
        this.plotShaper.bins.maxCasesInCells = maxCases;
        this.plotShaper.bins.numExcludedCases = numExcludedCases;
    }

    //calcSortOrder( attribute, arrayLength ) {
    //	"use strict";
    //	var sortArray = [],
    //		i,j=arrayLength;
    //
    //	for( i=0; i<j; i++ ) {
    //		sortArray.push( {index:i} );
    //	}
    //	if( attribute ) {
    //		Dyn.assert( attribute.cases.length === arrayLength );
    //		for( i=0; i<j; i++ ) {
    //			sortArray[i].value = attribute.cases[i];
    //		}
    //		sortArray.sort( Dyn.dataUtil.compareElementsByValue );
    //	}
    //	return sortArray;
    //},

    /**
     * Sort the case indices in the cell.
     * Updates cell.cases[], in order of ascending Attribute value.
     * @param cell {*} - Dyn.PlotShaper.bins.cells[] element
     * @param attributes {Dyn.Attribute|null} - attribute to sort by
     * @param caseIndices {[{caseIndex}]} - position data with caseIndex.
     * @param [isReverseSort] {Boolean} - optional parameter, if true sort descending instead of ascending
     */
    sortCasesInCell( cell, attributes, caseIndices, isReverseSort ) {
        "use strict";
        let Dyn = this.dyn,
            isMultiSort = Array.isArray( attributes ),
            attribute = isMultiSort ? attributes[0] : attributes;
        Dyn.assert( attribute && caseIndices, "missing required parameters" );
        let sortArray = [],
            i, caseCount=cell.cases.length,
            k, caseIndex, sortValue,
            customCategoryOrder = Boolean( attribute.fixedCategories ),
            customCategories = attribute.statsPreFilter.categories;

        // create array of {index, value}
        for( i=0; i<caseCount; i++ ) {
            k = cell.cases[i];
            caseIndex = caseIndices[k].caseIndex; // Note: caseIndex != k for multi-attribute plots.
            sortValue = attribute.cases[caseIndex]; // sort on attribute value (numeric or categorical
            // Checking customCategories exists or not, as if the attribute
            // is changed to numeric, it won't have categories.
            if( customCategoryOrder && customCategories.length > 0 ) {
                sortValue = customCategories.indexOf( sortValue ); // sort on custom category index 0, 1, 2, etc.
            }
            let sortEl = {
                index: k,
                value: sortValue
            };
            if( isMultiSort ) {
                attributes.forEach( (attr, index) => sortEl["value"+(index+1)] = attr.cases[caseIndex] );
            }
            sortArray.push( sortEl );
        }
        sortArray.sort( isReverseSort ?
            Dyn.dataUtil.compareElementsByValueReverse :
            Dyn.dataUtil.compareElementsByValue );

        // re-write cell.cases[] in new order
        for( i=0; i<caseCount; i++ ) {
            cell.cases[i] = sortArray[i].index;
        }

        return sortArray;
    }

    /**
     * Add a glow effect to the given adornment (assumed to be an adornment of our plot element).
     *
     * Ensures that each plot element has only one glow effect, and that the glow
     * is properly removed from the paper when this element is removed.
     *
     * Note: the element to glow can be a set (paper.set()), or individual element
     * this.glowElement will then be an Array, or individual glow elements, or null.
     *
     * @param elementToGlow -- rafael element to glow, or set of elements or array of elements
     * @param glowColor -- glow color (usually the same as the element color)
     * @param [glowWidth] {number} -- optional glow width
     */
    addGlow( elementToGlow, glowColor, glowWidth ) {
        "use strict";
        this.removeGlow(); // remove previous glow, if any

        let Dyn = this.dyn,
            glowAttrs = {
            color:glowColor,
            width: (glowWidth || Dyn.plotUtil.glowWidth)
        };

        if( Array.isArray( elementToGlow ) || (elementToGlow.type === "set")) {
            // glow each visible element of set or array, this.glowElement = [Raphael Element,...]
            //      note 1: a set is Array-like but does not pass Array.isArray() test
            //      note 2: certain rafael elements can't be glowed, e.g. text elements, glow() returns null.
            this.glowElement = [];
            elementToGlow.forEach( (el) => {
                let glowEl = Dyn.plotUtil.isElementVisible( el ) && el.glow( glowAttrs );
                if( glowEl ) {
                    this.glowElement.push( glowEl );
                }
            });
        } else {
            // glow an individual element, , this.glowElement = Raphael Element
            this.glowElement = elementToGlow.glow( glowAttrs );
        }
    }

    /**
     * Remove the glow element(s) created by addGlow()
     */
    removeGlow() {
        "use strict";
        if( Array.isArray( this.glowElement )) {
            // remove all glow elements in the set
            this.glowElement.forEach( el => el.remove());
        }
        else if( this.glowElement ) {
            // remove the single glow element
            this.glowElement.remove();
        }
        this.glowElement = null;
    }

    /**
     * Allow plot elements to clear selected adornments, if any.
     */
    clearSelectedAdornments() {
        "use strict";
        // derevied objects must implement this if they have selectable adornments
    }

    /**
     * Does this plot element allow use of a custom "order by {{attribute}}" setting?
     * Plot elements of type kControlsShape should override if needed.
     * @returns {boolean}
     */
    displayOrderBy() {
        "use strict";
        return false; // default to true, subclasses can override this
    }

    /**
     * Is the plot element using a custom "order by {{attribute}}" setting?
     * @returns {boolean}
     */
    hasCustomOrderBy() {
        "use strict";
        return( this.displayOrderBy() && (null !== this.plotShaper.dataSet.getOrderByAttribute()));
    }

    /**
     * Does the plot element allows user of custom "order by {{attribute}}" via setting?
     * plot elements of type kControlShape should override if needed.
     */
    allowCustomOrderBy() {
        "user strict";
        return false;
    }

    /**
     * Should this plot element/plot annotation drag event handler do nothing, ignoring the event?
     * @return {boolean}
     */
    ignoreDragEvents() {
        "use strict";
        // don't allow dragging of the plot elements annotations, etc, when in drag-map mode
        var Dyn = this.dyn,
            plotView = Dyn.plotView;
        return( plotView.isInDragMapMode() );
    }

    /**
     * Allow the plot to prepare for dragging this element, by changing settings.
     * To be called by a plot annotation's hover in and hover out event handlers, etc.
     * @param isHoverIn -- is hover in, else assume is hover out
     */
    prepareForDragEvents( isHoverIn ) {
        this.plotShaper.prepareElementsForDrag( isHoverIn );
    }

    /**
     * Get a modifiable reference to the plot adornments array for this plot element.
     * The plot adornments array is a 0+ length array of Raphael elements in the plot, e.g. lines for line plot element, text for the count element, etc.
     * The adornments array is created and owned by the Dyn.PlotView, so this objection should not store the array but
     * instead get a reference to it as needed.
     * @returns []
     */
    getAdornmentsArray() {
        return this.plotShaper.adornments.getAdornmentsArray( this.primaryType );
    }

    /**
     * Bring all the adornments associated with this plot element to the front.
     * Called once for each layer for each plot element. The default is to have all adornments in layer 0.
     * PlotElements can override this to put some elements above (in layer 1), via setAdornmentLayer()
     * @param adornmentsArray
     * @param layerNum - layer number, as managed by PlotShaper.numAdornmentLayers
     */
    layerAdornments( adornmentsArray, layerNum ) {
        let plotElement = this,
            debugLayerAdornments = this.plotShaper.debugLayerAdornments;

        // bring all elements in the current layerNum to the front
        adornmentsArray.forEach( (adorn, adornIndex) => {
            if( adorn.type==='set' ) {
                adorn.forEach( (member, indexInSet) => {
                    if( isInLayer( member, layerNum ) ) {
                        setLayer( member, layerNum, adornIndex, indexInSet );
                    }
                });
            } else if( isInLayer( adorn, layerNum )) {
                setLayer( adorn, layerNum, adornIndex );
            }
        } );

        function isInLayer( visibleElement, layerNum ) {
            // is the plot element in the layer? If no layer specified, it is in layer 0
            return layerNum === (visibleElement.data("layerNum") || 0);
        }

        function setLayer( visibleElement, layerNum, adornIndex, indexInSet) {
            // layer the element, by bringing it to the front, in layer order
            if( debugLayerAdornments ) {
                console.log( "layerAdornments layer:", layerNum, plotElement.primaryType, adornIndex, indexInSet||0,
                    visibleElement.type,
                    visibleElement.attr( "text" ) || visibleElement.data( "caseIndex" ) || visibleElement.data( "tooltip" ) || '' );
            }
            visibleElement.toFront();
        }
    }

    /**
     * Tell plot elements to hide or show any controls.
     * @param mode {{Dyn.PlotViewMode} | {Dyn.PlotModeImageReady}}
     */
    setViewMode( mode ){
        // derived elements should override if necessary
    }

    /**
     * Turn OFF the given toggle controls in this plot element,
     * e.g. turn off "Count by Histogram Section" in Dyn.CountElement()
     * or "Percent by Histogram Section" in Dyn.PercentElement()
     * See also ToolbarComponent.menuValueDict[].extraType for counts/percents that are turned ON.
     * See replaces() for use of turnOffToggleControl() to turn off and remove a plot element
     * @param iExistingPlotElement {Dyn.PlotElement} -- element to test
     * @param controlsToTurnOff {Array} -- array of Dyn.EPlotControl elements to turn off if found
     * @return {boolean} -- return true if all controls are off for this plot element
     */
    turnOffToggleControl( iExistingPlotElement, controlsToTurnOff ) {
        let allControlsAreOff = false;
        controlsToTurnOff.forEach((control)=>{
            if( iExistingPlotElement[control]) {
                iExistingPlotElement.toggleControl(control); // turn off control
                allControlsAreOff = iExistingPlotElement.isAllControlsOff();
            }
        });
        return allControlsAreOff;
    }

    /** default comparison; derived objects can override in case there is more than one default */
    isDefaultTitleForPlot( title ) {
        let Dyn = this.dyn;
        return Boolean( this.kDefaultTitle && title===Dyn.loc(this.kDefaultTitle));
    }

    getContextMenuClickHandler() {
        return false; // derived objects can add a handler, default is none
    }

    /** get the undo action string. derived objects can override to return
     * a string dynamically depending on the context.
     * @param contextHints {*}
     * @returns {string}
     */
    getUndoAction( contextHints ) {
        return this.kUndoAction || "";
    }

    /** Unit test cases for checking data types and conversions. */
    static unitTest( Dyn ) {
        let unitTester = new Dyn.UnitTester( "Dyn.PlotElement.unitTest()" );
        let mis = { value: undefined },
            dash = { value: '-' },
            str = { value: 'string' },
            strA = { value: 'a'},
            strB = { value: 'b' },
            num0 = { value: 0 },
            num1 = { value: 1 };

        unitTester.test( Dyn.dataUtil.compareElementsByValue( mis, dash ) === 0, "ok: undefined and '-' are equal");
        unitTester.test( Dyn.dataUtil.compareElementsByValue( mis, mis ) === 0, "ok: undefined equal to undefined" );
        unitTester.test( Dyn.dataUtil.compareElementsByValue( mis, str ) === -1, "ok: undefined sorts betfore string" );
        unitTester.test( Dyn.dataUtil.compareElementsByValue( mis, num0 ) === -1, "ok: undefined sorts before number");
        unitTester.test( Dyn.dataUtil.compareElementsByValue( num1, num0 ) === 1, "ok: 1 sorts after 0" );
        unitTester.test( Dyn.dataUtil.compareElementsByValue( strB, strA ) === 1, "ok: 'b' sorts after 'a'" );

        unitTester.report();
        return true;
    }

} // class PlotElement

// class MixupElement moved to mixupElement.js on 12/29/2023 for v 3.1.0

export const wrapDynPlotElement = function(Dyn) {

//	Flags for 'whatChanged' parameter of Dyn.PlotView.UpdatePlotView(),
//		Used to coordinate PlotElement changes with view changes.
Dyn.EPlotChange = {
	kChAxisSize		: 'viewSize',		// plot view or axis/margin subviews changed size, need to change plot layout to match
	kChIconPosition	: 'iconPosition',	// icon positions need changing (e.g. because of data values or plot type change)
	kChIconColor	: 'iconColor',		// icon color needs changing (e.g. Attribute selected/deselected, value change, etc)
	kChDataSize		: 'dataSize',		// data size has changed (number of cases or number of attributes changed)
	kChDataValue	: 'dataValue',		// data values of cases have changed
	kChNoAnimation	: 'noAnimation',	// change w/o animation requested (default is to animate case icons, etc.
	kChSlowAnimation: 'slowAnimation'	// slower animation requested
};

//	values for Dyn.PlotElement.priority, to form a natural calculation order when an array of Dyn.PlotElement is sorted
//		lower number is calculated first, adornments are on the bottom layer
//		higher numbers are calculated last, and their adornments are on top (z-axis)
//      E.g. if Mean and Mode lines overlap, Mean is visible, obscuring Mode, because Mean has a higher priority number.
//      NOTE: values should NOT be saved with PlotElement.save(), so numbers can easily adjusted in future versions.
Dyn.EPlotPriority = {
	kPriorityMixup 			: 20,	// (0) lowest priority, calculate mixup first
	kPriorityShape			: 30,	// (1) for separators, calculate bins next
	kPriorityMap			: 35,	// for map rendering
	kPriorityCount			: 50,	// for the count function
	kPriorityPercentage		: 60,	// for the percentage function
    // Population stats below Sample stats so overlap shows Sample Stats
    kPriorityPopulationInterQuartRange    : 70,	// for the interquartile range line on the plot
    kPriorityPopulationVariance : 74,	// for the variance line on the plot
    kPriorityPopulationStDeviation  : 72,
    // Population averages above population ranges so PopulationMean visible over PopulationStDeviation, Median visible over IQR.
    kPriorityPopulationMedian   : 80,
    kPriorityPopulationMean : 82,  // for the population mean in case of sample
    // Standard/Sample Ranges
    kPriorityInterQuartRange: 90,	// for the interquartile range line on the plot
    kPriorityVariance		: 92,	// for the variance line on the plot
	kPriorityStDeviation	: 94,	// for the standard deviation on the plot
    kPriorityStdError       : 95,
    kPriorityMeanAvDev      : 96,
    // Averages above Ranges (e.g. Mean above StDeviation so StDeviation does not obscure Mean)
    kPriorityMode			: 100, 	// for the mode line on the plot (Mode below other averages because there may be many modes)
    kPriorityMedian			: 102,	// for the median line on the plot
	kPriorityMean			: 104,	// for the mean line on the plot (Mean above St. Deviation so stdev does not obscure mean)
    // movable lines above Statistics, so they can be dragged without interference
    kPriorityDividers		: 108,	// for the divider element on the plot
	kPriorityXRefLine		: 110,	// priority to create reference line
	kPriorityYRefLine		: 120,	// priority to create reference line
	kPriorityLeastSquares	: 130,	// priority to create least squares line
    kPriorityPlotFunction   : 135,  // priority for plot function (plot above Least Squares, below movable line)
    kPriorityRuler          : 138,  // priority for ruler line, above ref lines, below movable line
	kPriorityDiagRefLine	: 140,	// for movable line, which we want on top since it is draggable
	kPriorityFreehandLine	: 150,	// for freehand/drawn line

	kPriorityLAST			: 200   // note highest priority for debugging purposes
};

//	values for Dyn.PlotElement.replaceType and Dyn.PlotElement[ property ] testing.
//      WARNING: values ARE saved with PlotElement.save(), do not delete or rename, instead move to OBSOLETE section (and follow example of other OBSOLETE values in code)
Dyn.EPlotControl = {
    isControlType: function(t) { // is t a value of EPlotControl, e.g. 'controlsMixup'?
        return Boolean((typeof t==='string') && Object.entries(Dyn.EPlotControl).find( e  => (e[1]===t)));
    },
    isControlProperty: function(p) { // is p a property of EPlotControl, e.g. 'kControlsMixup'?
        return Boolean((typeof p==='string') && Object.entries(Dyn.EPlotControl).find( e  => (e[0]===p)));
    },
	//--- Empty control key
	kControlsNothing: '',      // for elements not yet fully initialized

    // OBSOLETE but saved values, handle these values on PlotElement.restore() for backward compatibility (loading old .save() values)
    // when handling backward compatibility, kControlsXYZ is renamed to kControlsOldXYZ, but 'controls...' value must remain as is.
    kControlsOldBestFit: 'controlsBestFit', // NOTE: 'controlsBestFit' is an old name, retained to avoid breaking load of saved plots before 2016-06-20
    kControlsOldFreehandLine: 'controlsFreehandLine', // NOTE: 'controlsFreehandLine' is an old name, retained to avoid breaking load of saved plots before 2017-06-29
    kControlsOldUnstackedBarChart: 'controlsUnstackedBarChart', // NOTE: 'controlsUnstackedBarChart' is an old name, retained to avoid breaking load of saved plots before 2020-03-17
    kControlsOldStackedBarChart: 'controlsStackedBarChart', // NOTE: 'controlsStackedBarChart' is an old name, retained to avoid breaking load of saved plots before 2020-03-17

	//--- Primary control keys, one for each PlotElement subclass
	kControlsMixup: 'controlsMixup',        // element controls random/user-placed locations in plot
	kControlsDotPlot: 'controlsDotPlot',	// includes dot plots and scatterplots
    kControlsScatterPlot: 'controlsScatterPlot', // scatter plot
    kControlsBubbleChart: 'controlsBubbleChart', // bubble chart
    kControlsLineGraphSingle: 'controlsLineGraphSingle',
    kControlsLineAndCategoryGraph: 'controlsLineAndCategoryGraph',
    kControlsBarChartOfFreq: 'controlsBarChartOfFreq', // bar length = count of cases, stacked
    kControlsBarChartOfValues: 'controlsBarChartOfValues', // bar length = numeric attribute value, unstacked
	kControlsBarChartOfSums: 'controlsBarChartOfSums', // bars length = sum of attribute values of cases in stack
    kControlsBarChartOfMeans: 'controlsBarChartOfMeans', // bar length = mean of attribute values of cases in stack
	kControlsStretchedBarChart: 'controlsStretchedBarChart', // stacked, relative bar chart
    kControlsFreqHistogram: 'controlsFreqHistogram', // for old histograms and new Frequency Histograms
    kControlsPropHistogram: 'controlsPropHistogram', // Relative Proportion/Relative Frequency (Proportional axis)
    kControlsPercHistogram: 'controlsPercHistogram', // Relative Percent (Percentage axis)
	kControlsPieChart: 'controlsPieChart',
	kControlsBoxOnlyPlot: 'controlsBoxOnlyPlot',
	kControlsBoxAndOutlierPlot: 'controlsBoxAndOutlierPlot',
	kControlsBoxAndDotPlot: 'controlsBoxAndDotPlot',
	kControlsCount: 'controlsCount',
	kControlsPercentage: 'controlsPercentage',
	kControlsMean: 'controlsMean',
	kControlsMedian: 'controlsMedian',
	kControlsMode: 'controlsMode',
    kControlsMeanAvDev: 'controlsMeanAvDev',
	kControlsStDeviation: 'controlsStDeviation',
    kControlsVariance: 'controlsVariance',
    kControlsInterQuartRange: 'controlsInterQuartRange',
    kControlsPopulationMean: 'controlsPopulationMean',
    kControlsPopulationMedian: 'controlsPopulationMedian',
    kControlsPopulationStDeviation: 'controlsPopulationStDeviation',
    kControlsPopulationVariance: 'controlsPopulationVariance',
    kControlsPopulationInterQuartRange: 'controlsPopulationInterQuartRange',
    kControlsStdError: 'controlsStdError',
	kControlsRefLine: 'controlsRefLine',	// common control to add/remove all
	kControlsXRefLine: 'controlsXRefLine',
	kControlsYRefLine: 'controlsYRefLine',
    kControlsY2RefLine: 'controlsY2RefLine',
	kControlsDiagRefLine: 'controlsDiagRefLine',
    kControlsDiagRefLineY2: 'controlsDiagRefLineY2',
	kControlsLeastSquares: 'controlsLeastSquares', // see also kControlsOldBestFit below
	kControlsDividers: 'controlsDividers',
    kControlsAnnotations: 'controlsAnnotations', // see also kControlsOldFreehandLine below
	kControlsMap: 'controlsMap',
    kControlsMapOnly: 'controlsMapOnly',
    kControlsMapAndBubble: 'controlsMapAndBubble',
    kControlsMapAndLine: 'controlsMapAndLine',
    kControlsPlotFunction: 'controlsPlotFunction',
    kControlsGridLines: 'controlsGridLines',
    kControlsRulerX: 'controlsRulerX',	// ruler display for X-axis
    kControlsRulerY: 'controlsRulerY', // ruler display for Y-axis

	//--- Combination control keys: we define them here to simplify logic of searching for plot elements
	kControlsShape: 'controlsShape',	   		// element can separate (make bins), or a continuous axis
	kControlsMargins: 'controlsMargins',		// element can adjust margins during calcAxis().
	kControlsCaseOrder: 'controlsCaseOrder',	// element re-sorts cases when selected attribute changes
    kControlsHistogram: 'controlsHistogram',    // for all histogram types
	kControlsLineOrder: 'controlsLineOrder',	// element re-sorts line order when selected attribute changes for line graph
    kControlsLineGraph: 'controlsLineGraph',    // elements with lines for categories or not
	kControlsBarChart: 'controlsBarChart',   	// elements with stacked or unstacked bars
	kControlsBoxPlot: 'controlsBoxPlot',		// elements with boxes, with or without outliers or dots
	kControlsAverage: 'controlsAverage',		// for mean, median, mode, sd, population mean, median, standard deviation
	kControlsAdornmentPositionOnly: 'controlsAdornPosOnly',	// element with only overlays/adornments, does not affect icon position.
    kControlsY2Axis: 'controlsY2Axis',          // element that can use the Y2 axis, indicating that plot view should provide one

	//--- Feature control keys, one per button on toolbar, but may disappear or appear from plotElement classes.
    kControlsCellCount: 'controlsCellCount',
    kControlsSectionCount: 'controlsSectionCount',
    kControlsSectionSum: 'controlsSectionSum',
    kControlsSectionPercentage: 'controlsSectionPercentage',
    kControlsSectionProportion: 'controlsSectionProportion',
    kControlsCellPercentage: 'controlsCellPercentage',
    kControlsColPercentage: 'controlsColPercentage',
    kControlsRowPercentage: 'controlsRowPercentage',
    kControlsMultiCaseSections: 'controlsMultiCaseSections',    // plot element makes 1 graphical element per section (group of 1+ cases), instead of 1 per case/attrib combination.
    kControlsNumericIntervals: 'controlsNumericIntervals',       // plot element makes numeric intervals to create sections (e.g 0-9, 10-19) , implies kControlsMultiCaseSections
    kControlsLeastSquaresByAttribute: 'controlsLeastSquaresByAttribute',
    kControlsLeastSquaresByCategory: 'controlsLeastSquaresByCategory',
    kControlsAnnotatePencil: 'controlsAnnotatePencil',
    kControlsAnnotateRect: 'controlsAnnotateRect',
    kControlsAnnotateCircle: 'controlsAnnotateCirle',
	kControlsAnnotateArrow: 'controlsAnnotateArrow',
	kControlsAnnotateStraightLine: 'controlsAnnotateStraightLine',
    kControlsAnnotateText: 'controlsAnnotateText',
    kControlsAnnotateImage: 'controlsAnnotateImage',
    kControlsAnnotateCard: 'controlsAnnotateCard',
    kControlsShowSquares: 'controlsShowSquares',
    kControlsBubbles: 'controlsBubbles', // e.g. bubble chart or map with bubbles
};

// EMeasurePriority: sort order values to determine where a measure set appears in the Summary Table.
// Each PlotElement subclass has a Dyn.PlotElement.primaryType, and a matching measures array (Dyn.PlotMeasures.getMeasuresArray())
//      NOTE: values should NOT be saved with PlotElement.save(), so numbers can easily be adjusted in future versions.
Dyn.EMeasurePriority = {
    // Dyn.PlotElement.primaryType  : sort order (ascending)
    controlsCount			: 10,	// kControlsCount element: controls counts if no percents or dividers
    controlsPercentage		: 20,	// kControlsPercentage element: controls both counts and percents if both on, dividers off
    controlsDividers		: 30,	// kControlsDividers element: controls both counts and percents when dividers are on
    controlsMean			: 40,   // In general, follow the order of the Functions menu...
    controlsPopulationMean  : 42,   // except sample vs population go together
    controlsMedian			: 44,
    controlsPopulationMedian : 46,
    controlsMode			: 48,
    controlsMeanAvDev       : 49,
    controlsInterQuartRange : 52,
    controlsPopulationInterQuartRange : 53,
    controlsStDeviation	    : 55,
    controlsPopulationStDeviation : 56,
    controlsVariance	    : 58,
    controlsPopulationVariance : 59,
    controlsStdError        : 62,
    controlsBoxOnlyPlot     : 80,   // for Q1, Q2, Q3, etc.
    controlsBoxAndOutlierPlot : 80,
    controlsBoxAndDotPlot   : 80,
    controlsLeastSquares	: 90,
    controlsPlotFunction    : 91,
    controlsDiagRefLine     : 95,
    controlsDiagRefLineY2   : 96,
    controlsXRefLine        : 100,
    controlsYRefLine        : 100,
    controlsY2RefLine       : 100,
    controlsRulerX          : 110,
    controlsRulerY          : 120,
};

    Dyn.PlotElement = PlotElement;

    // Dyn.MixupElement assignment moved to mixupElement.js on 12/29/2023

    Dyn.debugUnitTestHasRun_PlotElement = ( Dyn.debugging ? PlotElement.unitTest( Dyn ) : false );

    /** abstract base class for PieSection and BarSection */
    class PlotSection {
        constructor( category, isSum ) {
            this.caseCount = 0; // how many cases in this section of the plot
            this.category = category; // which category if section is for a category
            this.isSum = isSum; // is this section a sum of values?
            this.valueSum = 0; // sum of values, if section is a sum of numeric case values
            this.textColor = Dyn.plotUtil.countTextColorDark;
        }

        /**
         * Respond to a change in the color of the section of a Bar or Pie Chart.
         * @param caseColor - from Dyn.colorUtil.calcCaseColor()
         * @param minContrast - [0-255] to match getColorBrightness()
         * @returns {boolean} - true if text color changed
         */
        updateColors( caseColor, minContrast ) {
            // update the embedded text color to have the right contrast #2102
            let oldTextColor = this.textColor;
            Dyn.assertFiniteNumeric( caseColor.b255, "caller should use calcColorBrightness()");
            this.textColor = (caseColor.b255 < minContrast)
                ? Dyn.plotUtil.countTextColorLight
                : Dyn.plotUtil.countTextColorDark;
            // if( this.textColor !== oldTextColor )
            //     console.log("PlotSection.updateColors", caseColor.colorString, "brightness:"+Dyn.colorUtil.getColorBrightness( caseColor ), this.textColor );
            return this.textColor !== oldTextColor; // true if text color changed
        }

        static sectionParams = new Set();
        static registerSectionName( paramName ) {
            this.sectionParams.add( paramName );
        }
        static createSections( cell, paramName ) {
            Dyn.assert( this.sectionParams.has(paramName), "unknown section name, needs registerSectionName("+paramName+")");
            this.removeOldSectionsFromCell( cell );
            cell[ paramName ] = []; // create a new array of sections
        }
        static removeOldSectionsFromCell( cell ) {
            this.sectionParams.forEach( name => {
                //console.log( "erasing cell."+name );
                delete cell[ name ]
            } );
        }
    }
    Dyn.PlotSection = PlotSection


} // wrapDynPlotElement
